"use client";

import { useState } from "react";
import { LuSlidersHorizontal } from "react-icons/lu";
import {
  activeRepresentativeFilterCount,
  type RepresentativeFilters,
} from "@/hooks/useRepresentativeFilters";

const COUNTRY_LABEL: Record<string, string> = { "United States": "USA" };
const countryLabel = (c: string) => COUNTRY_LABEL[c] ?? c;

const INSTITUTION_TYPES = ["University", "Student Community", "Organization"];

const SERVICE_CATEGORIES = [
  "Application Writing",
  "CV & Profile Review & Revamp",
  "Visa Support",
  "Full Application Support",
  "Scholarship Support",
  "Financial Documentation Support",
  "Language Test Preparation",
  "Academic Test Preparation",
  "Interview Preparation",
];

const LIMIT = 5;

function ShowMoreList({
  items,
  selected,
  onToggle,
  emptyText,
  display,
}: {
  items: string[];
  selected: string[];
  onToggle: (v: string) => void;
  emptyText: string;
  display?: (v: string) => string;
}) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, LIMIT);
  const overflow = items.length - LIMIT;

  if (items.length === 0) {
    return <p className="text-xs text-gray-400">{emptyText}</p>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {visible.map((v) => (
        <label
          key={v}
          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
        >
          <input
            type="checkbox"
            checked={selected.includes(v)}
            onChange={() => onToggle(v)}
            className="rounded border-gray-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="truncate">{display ? display(v) : v}</span>
        </label>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="mt-0.5 text-left text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {expanded ? "Show less" : `+${overflow} more`}
        </button>
      )}
    </div>
  );
}

export type RepresentativeFilterPanelProps = {
  countries: string[];
  fields: string[];
  services: string[];
  filters: RepresentativeFilters;
  setFilters: (
    update:
      | Partial<RepresentativeFilters>
      | ((prev: RepresentativeFilters) => RepresentativeFilters)
  ) => void;
  clear: () => void;
  activeCount: number;
};

export function RepresentativeFilterPanel({
  countries,
  fields,
  services,
  filters,
  setFilters,
  clear,
  activeCount,
}: RepresentativeFilterPanelProps) {
  const toggle = (
    key: "countries" | "fields" | "institutionTypes" | "services",
    v: string
  ) =>
    setFilters((prev) => ({
      ...prev,
      [key]: prev[key].includes(v)
        ? prev[key].filter((x) => x !== v)
        : [...prev[key], v],
    }));

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LuSlidersHorizontal className="w-4 h-4 text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm uppercase tracking-wide">
            Filters
          </h3>
        </div>
        {activeCount > 0 && (
          <button
            onClick={clear}
            className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Institution type — always small list, no show-more needed */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Institution type
        </h4>
        <div className="flex flex-col gap-1.5">
          {INSTITUTION_TYPES.map((t) => (
            <label
              key={t}
              className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none"
            >
              <input
                type="checkbox"
                checked={filters.institutionTypes.includes(t)}
                onChange={() => toggle("institutionTypes", t)}
                className="rounded border-gray-300 dark:border-white/20 text-indigo-600 focus:ring-indigo-500"
              />
              <span>{t}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Countries */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Country
        </h4>
        <ShowMoreList
          items={countries}
          selected={filters.countries}
          onToggle={(v) => toggle("countries", v)}
          emptyText="No countries in dataset"
          display={countryLabel}
        />
      </div>

      {/* Fields */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Fields offered
        </h4>
        <ShowMoreList
          items={fields}
          selected={filters.fields}
          onToggle={(v) => toggle("fields", v)}
          emptyText="No fields in dataset"
        />
      </div>

      {/* Service categories */}
      <div>
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
          Service category
        </h4>
        <ShowMoreList
          items={services.length > 0 ? services : SERVICE_CATEGORIES}
          selected={filters.services}
          onToggle={(v) => toggle("services", v)}
          emptyText="No services in dataset"
        />
      </div>
    </div>
  );
}

export { activeRepresentativeFilterCount };
